import React, { useLayoutEffect, useEffect } from 'react';
import styles from './collections.module.scss';

import {
  FocusContext,
  useFocusable,
  setFocus,
  doesFocusableExist,
  getCurrentFocusKey,
  pause,
  resume
} from '@noriginmedia/norigin-spatial-navigation';
import { focusKeyPrevAtom } from '@smart-tv/atoms';
import ContentSliderItem from '@smart-tv/components/content-slider/content-slider-item';
import { handleFocusKeyPrev } from '@smart-tv/components/program-sheet/_utils/focusKeyUtils';
import Typography from '@smart-tv/components/typography/typography';
import {
  ActionableContentConsumer,
  ActionableContentProvider
} from '@smart-tv/contexts/actionable-content-context';
import testIds from '@smart-tv/e2e/shared/variables';
import useCollections from '@smart-tv/hooks/use-collections';
import useDialog from '@smart-tv/hooks/use-dialog';
import useEventListener from '@smart-tv/hooks/use-event-listener';
import useUiElementContent from '@smart-tv/hooks/use-ui-element-content';
import { UIElement } from '@smart-tv/typings/content';
import { rootStore as stripesRootStore } from '@smart-tv/components/stripes/_stores/store-root';

import { usePlayerContext } from '../player/_contexts/player-context';
import ProgressSpinner from '../progress-spinner/progress-spinner';
import { LIVE_TV_STRIPE_ID } from '../stripes/_utils/types';
import { IconButton } from '@smart-tv/components/icon-button/icon-button';
import { useAtom } from 'jotai';
import { IS_BROWSER } from '@smart-tv/utils/is-browser';
import { ArrowBackIcon } from '../icons/arrow-back-icon';
import { getLayout } from '@smart-tv/utils/kmm/helpers';
import { ContentLayout } from '@smart-tv/typings/content';
import clsx from 'clsx';
import { CollectionsSkeleton } from './collections-skeleton';
import { isBackKey } from '@smart-tv/utils/remoteKeys';

interface Props {
  uiElement: UIElement;
  isProvider?: boolean;
}

export const Collections: React.FC<Props> = (props: Props) => {
  const { uiElement } = props;

  const isLayoutPortrait = getLayout(uiElement.style) == ContentLayout.Portrait;
  const [loadingIndicatorLoaded, setLoadingIndicatorLoaded] = React.useState(false);
  const previousItemCountRef = React.useRef<number>(0);

  const { uiElementContent, loaderRef, isFetching, handleFavoritesUpdate } = useUiElementContent(
    uiElement.id,
    uiElement.contentSource,
    true,
    uiElement.disablePagination,
    uiElement.providerContentProps,
    uiElement.style,
    loadingIndicatorLoaded
  );
  const collectionsLoaded = Boolean(uiElementContent);

  useEffect(() => {
    document.body.style.setProperty('overflow', 'hidden');
    if (handleFavoritesUpdate && uiElement.properties?.update_on_favorite) {
      stripesRootStore.stripesStore.registerStripeUpdateCallback(
        uiElement.id,
        handleFavoritesUpdate
      );
    }
    return () => {
      document.body.style.removeProperty('overflow');
      if (handleFavoritesUpdate && uiElement.properties?.update_on_favorite) {
        stripesRootStore.stripesStore.unregisterStripeUpdateCallback(
          uiElement.id,
          handleFavoritesUpdate
        );
      }
    };
  }, [uiElement.id, uiElement.properties?.update_on_favorite, handleFavoritesUpdate]);

  const { player } = usePlayerContext();
  const { opened: openDialog, handleSetDialogFocusKey } = useDialog();
  const { openCollections, handleClose } = useCollections();
  const [focusKeyPrev, setFocusKeyPrev] = useAtom(focusKeyPrevAtom);

  // Handle focus restoration when items are removed
  useEffect(() => {
    const currentItemCount = uiElementContent?.items?.length ?? 0;

    // Only handle focus restoration if collection is open
    if (!openCollections) {
      previousItemCountRef.current = currentItemCount;
      return;
    }

    // If items were removed (count decreased)
    if (previousItemCountRef.current > 0 && currentItemCount < previousItemCountRef.current) {
      // Don't try to restore focus if there are no items
      if (currentItemCount === 0) {
        previousItemCountRef.current = currentItemCount;
        return;
      }

      // Get the currently focused item's index
      const currentFocusKey = getCurrentFocusKey();
      const match = currentFocusKey?.match(/^(\d+)-collection$/);
      let targetIndex = match ? Number(match[1]) : 0;

      // If the focused item no longer exists, try the previous index
      if (targetIndex >= currentItemCount && targetIndex > 0) {
        targetIndex = targetIndex - 1;
      }

      // Clamp to valid range
      targetIndex = Math.max(0, Math.min(targetIndex, currentItemCount - 1));

      // Set focus to the target index
      const targetFocusKey = `${targetIndex}-collection`;
      if (doesFocusableExist(targetFocusKey)) {
        setFocus(targetFocusKey);
      }
    }

    previousItemCountRef.current = currentItemCount;
  }, [openCollections, uiElementContent?.items?.length]);

  const { focusKey, ref } = useFocusable({
    isFocusBoundary: true
  });

  const [animating, setAnimating] = React.useState(false);

  const scrollingRef = React.useRef<HTMLDivElement>(null);
  const skipInitialFocusScrollRef = React.useRef(false);
  const wasCollectionsOpenRef = React.useRef(false);

  useLayoutEffect(() => {
    if (!openCollections) {
      wasCollectionsOpenRef.current = false;
      return;
    }

    if (wasCollectionsOpenRef.current) return;
    wasCollectionsOpenRef.current = true;

    skipInitialFocusScrollRef.current = !focusKeyPrev?.isLiveTV;
    scrollingRef.current?.scrollTo({ top: 0, behavior: 'auto' });
  }, [openCollections, focusKeyPrev?.isLiveTV]);

  useEffect(() => {
    if (openCollections && focusKeyPrev?.isLiveTV) {
      handleFocusKeyPrev(focusKeyPrev, openCollections, handleSetDialogFocusKey, setFocusKeyPrev);
    }
  }, [openCollections, focusKeyPrev, handleSetDialogFocusKey, setFocusKeyPrev]);

  // pause focus system while the loading screen is shown so we cant navigate while loading
  useEffect(() => {
    if (openCollections && !uiElementContent) {
      pause();
    } else {
      resume();
    }
    return () => {
      resume();
    };
  }, [openCollections, uiElementContent]);

  useLayoutEffect(() => {
    if (loaderRef.current && collectionsLoaded) {
      setLoadingIndicatorLoaded(true);
    }
  }, [collectionsLoaded, loaderRef, loadingIndicatorLoaded]);

  const scrolling = React.useCallback(
    (itemIndex: number) =>
      ({ node }: { node: HTMLElement }) => {
        if (skipInitialFocusScrollRef.current) {
          skipInitialFocusScrollRef.current = false;
          return;
        }

        if (scrollingRef.current !== null) {
          scrollingRef.current.scrollTo({
            top: itemIndex === 0 ? 0 : node.offsetTop - 285,
            behavior: 'auto'
          });
        }
      },
    [scrollingRef, skipInitialFocusScrollRef]
  );

  const handleItemArrowPress = React.useCallback(
    (direction: string, actionableItemsLength: number, itemIndex: number) => {
      if (actionableItemsLength - 1 === itemIndex && direction === 'right') {
        return false;
      }
      return true;
    },
    []
  );

  const handleKeyPress = React.useCallback(
    (event: KeyboardEvent) => {
      if (isBackKey(event) && openCollections && !player && !openDialog) {
        handleClose();
      }
    },
    [handleClose, openCollections, openDialog, player]
  );

  useEventListener('keydown', (event: KeyboardEvent) => handleKeyPress(event));

  if (!openCollections) return null;

  return (
    <div className={styles['collections']} data-testid={testIds.collections.root}>
      <div className={styles['scroll-content']} ref={scrollingRef}>
        <div>
          {IS_BROWSER && (
            <IconButton
              aria-label="Luk dialog"
              onClick={handleClose}
              className={styles['collections__button--close']}
              data-testid={testIds.collections.closeButton}
              icon={<ArrowBackIcon />}
            />
          )}
          <Typography
            className={styles['title']}
            variant="h2"
            data-testid={testIds.collections.title}
          >
            {uiElement.title}
          </Typography>
          {!uiElementContent ? (
            <CollectionsSkeleton uiElement={uiElement} />
          ) : (
            <FocusContext.Provider value={focusKey}>
              <ActionableContentProvider items={uiElementContent.items}>
                <ActionableContentConsumer>
                  {({ actionableItems }) =>
                    actionableItems?.length ? (
                      <div
                        className={clsx(
                          styles['collections__content'],
                          isLayoutPortrait
                            ? styles['collections__content--portrait']
                            : styles['collections__content--landscape']
                        )}
                        ref={ref}
                      >
                        {actionableItems?.map((item, index) => {
                          const focusKey = `${index}-collection`; // add collection suffix to avoid possible overlap with focus keys on stripe page
                          return (
                            <ContentSliderItem
                              key={index}
                              contentItem={item}
                              isUnavailable={
                                item.providerCount !== undefined && item.providerCount <= 0
                              }
                              showProgress={Boolean(uiElementContent.properties.showProgress)}
                              showStartTime={Boolean(uiElementContent.properties.showStartTime)}
                              showContentTitle={Boolean(
                                uiElementContent.properties.showContentTitle
                              )}
                              onFocus={scrolling(index)}
                              contentSliderIndex={index}
                              liveTV={uiElement.id === LIVE_TV_STRIPE_ID}
                              collections={true}
                              animating={animating}
                              setAnimating={setAnimating}
                              arrowPressHandler={(direction) =>
                                handleItemArrowPress(direction, actionableItems.length, index)
                              }
                              focusKey={focusKey}
                              stripePrimaryAction={uiElementContent.primaryAction}
                              handleFavoritesUpdate={handleFavoritesUpdate}
                              className={styles['collections__item']}
                            />
                          );
                        })}
                        {!uiElement.disablePagination && collectionsLoaded && (
                          <div className={styles['loader']} ref={loaderRef}>
                            {isFetching && <ProgressSpinner />}
                          </div>
                        )}
                      </div>
                    ) : null
                  }
                </ActionableContentConsumer>
              </ActionableContentProvider>
            </FocusContext.Provider>
          )}
        </div>
      </div>
    </div>
  );
};

export default Collections;
